import { mdiCalendarOutline, mdiChevronDown, mdiChevronUp, mdiCurrencyUsd, mdiDomain, mdiFileDocumentOutline, mdiFileUploadOutline, mdiLightningBolt, mdiPound, mdiReceiptTextOutline, mdiSwapHorizontal, mdiDownload } from '@mdi/js';
import Icon from '@mdi/react';
import { addDays, format } from 'date-fns';
import { observer } from 'mobx-react';
import * as React from 'react';
import { IRemitClaim, IRemitLine, postedCount } from '../../services/remits/remit-claims';
import { IRemit, RemitKind } from '../../services/remits/remits-api';
import { ReconcileStore } from '../../stores/remits/reconcile.store';
import { RemitsStore } from '../../stores/remits/remits.store';
import { ButtonGroup, IButtonGroupItemProps } from '../base/button-group/button-group';
import { BaseButton } from '../base/button/button';
import { BaseInput } from '../base/input/input';
import { Notice } from '../base/notice/notice';
import { BaseSelect, ISelectOption } from '../base/select/select';
import { DataTable } from '../data-table/data-table';
import { DataTablePresenter, ITableColumn } from '../data-table/data-table.presenter';
import { EraUploadDialog } from '../era-upload/era-upload-dialog';
import { TabContainer, tabElementId } from '../tab-container/tab-container';
import { ReconcileExportDialog } from './reconcile-export-dialog';
import { ReconcilePanel } from './reconcile-panel';
import { RemitDetail } from './remit-detail';
import { day, money } from './remit-format';
import { RemitLineDialog } from './remit-line-dialog';
import * as S from './remits.styles';

/** How a console opens a dialog: its own dialog host, called with these options. */
export interface IRemitsDialogOptions {
    onClose: () => void;
    disableBackdropClick?: boolean;
    disableEscapeKeyDown?: boolean;
    renderTitle?: string;
}

/**
 * What differs between the provider and Support consoles, passed in rather than forked (the Denials and Payers
 * pattern, GP-20/21). The endpoints differ too, and come in with the store's `api`.
 */
export interface IRemitsPageProps {
    store: RemitsStore;
    dialogs: {
        showDialog: (Component: React.ReactElement, options: IRemitsDialogOptions) => void;
        hideDialog: () => void;
        showError?: (err: Error) => void;
    };
    /** The page frame, row and card each console draws its pages with. */
    layout: {
        Page: React.ComponentType<{ children?: React.ReactNode }>;
        Row: React.ComponentType<{ children?: React.ReactNode }>;
        Card: React.ComponentType<{ size: 'large'; children?: React.ReactNode }>;
    };
    /**
     * Demo mode. The stores read the console's demo data (FE-178) — nothing real may render (NS-14) — so the page lists
     * as usual; only what has no demo version stays off: the PDFs, Upload 835 ERA, and Reconcile's writes (the store's).
     */
    isDemo: boolean;
    /** The claim's page on this console. */
    claimHref: (claimId: string) => string;
    /** The Reconcile tab's state. Without it the page has the Remits tab only — how a console turns Reconcile off. */
    reconcile?: ReconcileStore;
}

type RemitsTab = 'remits' | 'reconcile';
const TAB_PANEL_ID = 'remits-tab-panel';

const KIND_BUTTONS: Record<RemitKind, string> = { ERA: 'ERA', EOB: 'EOB' };

type DatePreset = 'any' | '7' | '30' | '90' | 'custom';
const DATE_OPTIONS: Array<ISelectOption<string>> = [
    { label: 'Any paid date', value: 'any' },
    { label: 'Last 7 days', value: '7' },
    { label: 'Last 30 days', value: '30' },
    { label: 'Last 90 days', value: '90' },
    { label: 'Custom range', value: 'custom' },
];

const isoDay = (date: Date): string => format(date, 'yyyy-MM-dd');

/** A typed check amount as the filter wants it: `$1,495.40` → `1495.40`; anything else is no filter. */
const amountFilter = (text: string): string | undefined => {
    const cleaned = text.replace(/[$,\s]/g, '');
    return /^\d+(\.\d{1,2})?$/.test(cleaned) ? cleaned : undefined;
};

/** A text filter that waits for typing to pause before it asks the server. */
const useDebounced = (value: string, delay = 400): string => {
    const [settled, setSettled] = React.useState(value);
    React.useEffect(() => {
        const timer = window.setTimeout(() => setSettled(value), delay);
        return (): void => window.clearTimeout(timer);
    }, [value, delay]);
    return settled;
};

const FilterField: React.FC<{
    icon: string;
    placeholder: string;
    value: string;
    width?: number;
    type?: string;
    min?: string;
    max?: string;
    errorMessage?: string;
    onChange: (value: string) => void;
}> = ({ icon, placeholder, value, width, type, min, max, errorMessage, onChange }) => (
    <S.FilterSlot width={width}>
        <BaseInput
            compact={true}
            iconPath={icon}
            type={type}
            min={min}
            max={max}
            errorMessage={errorMessage}
            disableLatin1Validation={true}
            value={value}
            placeholder={placeholder}
            aria-label={placeholder}
            onChange={(event: React.ChangeEvent<HTMLInputElement>): void => onChange(event.target.value)}
        />
    </S.FilterSlot>
);

/** The Claims column: a count only once the remit's claims have loaded (crosswalk §1: no count on the list). */
const CLAIMS_FAILED = 'The claims on this remit could not be loaded. Open the row to try again.';

const ClaimsCell: React.FC<{ store: RemitsStore; remit: IRemit }> = observer(({ store, remit }) => {
    const state = store.claims.get(remit.id);
    if (state?.error) {
        return <S.Amount title={CLAIMS_FAILED}>{'\u2014'}</S.Amount>;
    }
    return <S.Amount>{state?.claims ? state.claims.length : '\u2026'}</S.Amount>;
});

const PostedCell: React.FC<{ store: RemitsStore; remit: IRemit }> = observer(({ store, remit }) => {
    const state = store.claims.get(remit.id);
    const claims = state?.claims;
    if (state?.error) {
        return <S.Muted title={CLAIMS_FAILED}>Not loaded</S.Muted>;
    }
    if (!claims) {
        return <S.Muted>{'\u2026'}</S.Muted>;
    }
    const { posted, total } = postedCount(claims);
    const tone = total && posted === total ? 'all' : posted ? 'some' : 'none';
    return (
        <S.Posted tone={tone}>
            {posted} / {total} posted
        </S.Posted>
    );
});

/**
 * Remits & Reconcile (FE-163), the Remits tab: every ERA and EOB the practice received, one list per kind, each
 * opening in place to the claims it pays.
 *
 * Built from the crosswalk in `docs/remits-v1/`; what the design draws and the crosswalk does not map is not built,
 * and the ticket lists it — Upload EOBs, the auto-match note, and the claim lines PT RESP, ADJ TO TOTAL PREV PD,
 * INTEREST, LATE FILING CHARGE and NET. The footer's "Reconciled against N deposit accounts" belongs to Reconcile.
 */
/** Unpost's question, in the console's own dialog. */
const ConfirmBody: React.FC<{ message: string; action: string; onAnswer: (ok: boolean) => void }> = ({ message, action, onAnswer }) => (
    <S.Confirm>
        <p>{message}</p>
        <S.Actions>
            <BaseButton variant="outlined" size="compact" onClick={(): void => onAnswer(false)}>
                Cancel
            </BaseButton>
            <BaseButton variant="primary" size="compact" onClick={(): void => onAnswer(true)}>
                {action}
            </BaseButton>
        </S.Actions>
    </S.Confirm>
);

export const RemitsPage: React.FC<IRemitsPageProps> = observer(({ store, dialogs, layout, isDemo, claimHref, reconcile }) => {
    const { Page, Row, Card } = layout;
    const [tab, setTab] = React.useState<RemitsTab>('remits');
    const reconcileOn = !!reconcile;
    const shown: RemitsTab = reconcileOn ? tab : 'remits';
    // Counted on arrival, not on first opening: the tab's label carries the unposted deposits, as the design does.
    React.useEffect(() => {
        if (reconcile) {
            reconcile.reset();
            void reconcile.start();
        }
    }, [reconcile]);
    const tabs = [
        { id: 'remits', label: 'Remits', icon: mdiReceiptTextOutline },
        ...(reconcileOn
            ? [{ id: 'reconcile', label: 'Reconcile', icon: mdiSwapHorizontal, count: reconcile?.unpostedDeposits }]
            : []),
    ];
    const confirm = React.useCallback(
        (message: string, action: string): Promise<boolean> =>
            new Promise(resolve => {
                const answer = (ok: boolean): void => {
                    dialogs.hideDialog();
                    resolve(ok);
                };
                dialogs.showDialog(<ConfirmBody message={message} action={action} onAnswer={answer} />, {
                    onClose: (): void => answer(false),
                    renderTitle: action,
                });
            }),
        [dialogs],
    );
    const [payer, setPayer] = React.useState('');
    const [number, setNumber] = React.useState('');
    const [amount, setAmount] = React.useState('');
    const [preset, setPreset] = React.useState<DatePreset>('any');
    const [from, setFrom] = React.useState('');
    const [to, setTo] = React.useState('');
    const settledPayer = useDebounced(payer.trim());
    const settledNumber = useDebounced(number.trim());
    const settledAmount = useDebounced(amount);
    const amountInvalid = !!settledAmount.trim() && !amountFilter(settledAmount);

    // Every visit starts clean: the inputs above start empty, so the list behind them must too (the store outlives
    // the page). The table mounts only after, so its first fetch is the visit's first load.
    const [ready, setReady] = React.useState(false);
    React.useEffect(() => {
        store.reset();
        setReady(true);
    }, [store]);

    React.useEffect(() => {
        if (!ready) {
            return;
        }
        const today = new Date();
        // A range typed backwards still means the days between the two dates. "Last 7 days" is today and the six before
        // it, as in the shared date-range control: the server range is inclusive.
        const [first, last] = from && to && from > to ? [to, from] : [from, to];
        const paidFrom =
            preset === 'custom' ? first || undefined : preset === 'any' ? undefined : isoDay(addDays(today, 1 - Number(preset)));
        const paidTo = preset === 'custom' ? last || undefined : undefined;
        // Unchanged filters are a no-op in the store, so this asks the server only when the query really changed.
        store.setFilters({
            payerName: settledPayer || undefined,
            paymentNumber: settledNumber || undefined,
            amount: amountFilter(settledAmount),
            paidFrom,
            paidTo,
        });
    }, [store, ready, settledPayer, settledNumber, settledAmount, preset, from, to]);

    const kind = store.kind;
    const isEra = kind === 'ERA';

    const columns: ITableColumn<IRemit>[] = React.useMemo(
        () => [
            {
                id: 'Id',
                accessor: 'displayId',
                width: 150,
                Header: isEra ? 'ERA ID' : 'EOB ID',
                Cell: ({ row }): React.ReactElement => (
                    <S.IdCell kind={isEra ? 'ERA' : 'EOB'} title={row.original.displayId}>
                        <Icon path={isEra ? mdiLightningBolt : mdiFileDocumentOutline} size={0.7} />
                        <span>{row.original.displayId}</span>
                    </S.IdCell>
                ),
            },
            { id: 'Payer', accessor: 'payerName', width: 200, Header: isEra ? 'Payer name' : 'Payer on check' },
            {
                id: 'Paid',
                accessor: 'paidAmount',
                width: 120,
                Header: 'Paid amount',
                Cell: ({ row }): React.ReactElement => (
                    <S.Amount strong={true}>{money(store.paidOf(row.original))}</S.Amount>
                ),
            },
            { id: 'Type', accessor: 'paymentType', width: 70, Header: 'Type' },
            {
                id: 'PaidDate',
                accessor: 'paidDate',
                width: 120,
                Header: 'Paid date',
                Cell: ({ row }): React.ReactElement => <>{day(row.original.paidDate)}</>,
            },
            {
                id: 'Created',
                accessor: 'created',
                width: 120,
                Header: isEra ? 'Received' : 'Uploaded',
                Cell: ({ row }): React.ReactElement => <>{day(row.original.created)}</>,
            },
            isEra
                ? { id: 'Provider', accessor: 'providerName', width: 180, Header: 'Provider' }
                : { id: 'Number', accessor: 'paymentNumber', width: 180, Header: 'Payment #' },
            {
                id: 'Claims',
                width: 80,
                Header: 'Claims',
                Cell: ({ row }): React.ReactElement => <ClaimsCell store={store} remit={row.original} />,
            },
            {
                id: 'Posted',
                width: 130,
                Header: 'Posted',
                Cell: ({ row }): React.ReactElement => <PostedCell store={store} remit={row.original} />,
            },
            {
                id: 'Expand',
                width: 40,
                Header: '',
                Cell: ({ row }): React.ReactElement => (
                    <Icon path={(row as unknown as { isExpanded?: boolean }).isExpanded ? mdiChevronUp : mdiChevronDown} size={0.8} />
                ),
            },
        ],
        [store, isEra],
    );

    const [presenter] = React.useState(
        () => new DataTablePresenter<IRemit>({ name: 'remits', data: [], columns, filterSchemas: [] }),
    );
    React.useEffect(() => presenter.onColumnsUpdate(columns), [presenter, columns]);
    React.useEffect(() => {
        presenter.onDataUpdate(store.rows);
        presenter.onDataTotalChange(store.total);
    }, [presenter, store.rows, store.total]);

    const onFetch = React.useCallback(
        ({ pageIndex, pageSize }: { pageIndex: number; pageSize: number }): void => {
            store.setPage(pageIndex, pageSize);
        },
        [store],
    );
    // A new kind or filter set remounts the table on its first page. The table restores the page it last saved, so
    // that is cleared first — otherwise a filter applied on page 3 opens page 3 of the filtered list.
    const tableKey = `${kind}|${JSON.stringify(store.filters)}`;
    React.useMemo(() => presenter.saveTableDefaults({ pageIndex: 0, pageSize: store.pageSize }), [presenter, tableKey]); // eslint-disable-line react-hooks/exhaustive-deps

    const toggleRow = React.useCallback((row: unknown): void => {
        (row as { toggleRowExpanded: () => void }).toggleRowExpanded();
    }, []);

    const openLine = React.useCallback(
        (line: IRemitLine, claim: IRemitClaim, patient?: string): void => {
            dialogs.showDialog(<RemitLineDialog line={line} patient={patient} memberId={claim.subscriberId} />, {
                onClose: dialogs.hideDialog,
                renderTitle: 'Charge',
            });
        },
        [dialogs],
    );
    const renderDetail = React.useCallback(
        (row: { original: IRemit }): React.ReactElement => (
            <RemitDetail store={store} remit={row.original} openLine={openLine} claimHref={claimHref} pdfs={!isDemo} />
        ),
        [store, openLine, claimHref, isDemo],
    );

    const openUpload = (): void => {
        dialogs.showDialog(
            <EraUploadDialog
                clearinghouses={store.clearinghouses}
                upload={async (x12: string, clearinghouseId?: string): Promise<unknown> => {
                    if (!clearinghouseId) {
                        throw new Error('Select a clearinghouse to upload.');
                    }
                    const id = await store.api.uploadEra(clearinghouseId, x12);
                    void store.load();
                    return id;
                }}
                onError={dialogs.showError}
                onClose={dialogs.hideDialog}
            />,
            {
                onClose: dialogs.hideDialog,
                renderTitle: 'Upload 835 ERA',
                // A stray click outside must not discard a chosen file; Escape still closes.
                disableBackdropClick: true,
            },
        );
    };

    const kindButtons: IButtonGroupItemProps[] = (['ERA', 'EOB'] as RemitKind[]).map(each => ({
        label: `${KIND_BUTTONS[each]}${store.counts[each] !== undefined ? ` ${store.counts[each]?.toLocaleString()}` : ''}`,
    }));
    const clearinghouseOptions: Array<ISelectOption<string>> = [
        { label: 'All clearinghouses', value: '' },
        ...store.clearinghouses.map(clearinghouse => ({ label: clearinghouse.name || clearinghouse.id, value: clearinghouse.id })),
    ];

    return (
        <Page>
            <Row>
                <TabContainer
                    variant="underline"
                    tabs={tabs}
                    activeTab={shown}
                    onTabChange={(id): void => {
                        // Reconcile may have posted or unposted a remit, which changes its "Linked" in this list.
                        if (id === 'remits' && shown === 'reconcile') {
                            void store.load();
                        }
                        setTab(id as RemitsTab);
                    }}
                    panelId={TAB_PANEL_ID}
                    trailing={
                        shown === 'reconcile' && reconcile ? (
                            <S.Toolbar>
                                <BaseButton variant="outlined" size="compact" onClick={reconcile.flip}>
                                    <Icon path={mdiSwapHorizontal} size={0.7} />
                                    <span>Flip panels</span>
                                </BaseButton>
                                <BaseButton
                                    variant="outlined"
                                    size="compact"
                                    disabled={!reconcile.accounts.length}
                                    onClick={(): void =>
                                        dialogs.showDialog(
                                            <ReconcileExportDialog
                                                store={reconcile}
                                                clearinghouseName={store.clearinghouseName}
                                                onClose={dialogs.hideDialog}
                                            />,
                                            {
                                                onClose: dialogs.hideDialog,
                                                renderTitle: 'Export reconciled transactions',
                                                // Not closed by a stray click or Escape while a long export runs.
                                                disableBackdropClick: true,
                                                disableEscapeKeyDown: true,
                                            },
                                        )
                                    }>
                                    <Icon path={mdiDownload} size={0.7} />
                                    <span>Export</span>
                                </BaseButton>
                            </S.Toolbar>
                        ) : (
                            <S.Toolbar
                                title={
                                    isDemo
                                        ? 'Uploading an 835 is not available in demo mode.'
                                        : store.clearinghouses.length
                                        ? undefined
                                        : store.clearinghousesError
                                        ? 'The clearinghouses could not be loaded. Click to try again.'
                                        : 'This account has no clearinghouse to upload to.'
                                }>
                                <BaseButton
                                    variant="primary"
                                    size="compact"
                                    disabled={isDemo || (!store.clearinghouses.length && !store.clearinghousesError)}
                                    onClick={(): void => {
                                        if (store.clearinghouses.length) {
                                            openUpload();
                                        } else {
                                            void store.loadClearinghouses();
                                        }
                                    }}>
                                    <Icon path={mdiFileUploadOutline} size={0.7} />
                                    <span>Upload 835 ERA</span>
                                </BaseButton>
                            </S.Toolbar>
                        )
                    }
                />
            </Row>
            <Row>
                <S.TableFrame role="tabpanel" id={TAB_PANEL_ID} aria-labelledby={tabElementId(TAB_PANEL_ID, shown)}>
                    {shown === 'reconcile' && reconcile ? (
                        <>
                            {/* The design adds "ENTER auto-matches the rest"; the crosswalk defines no auto-match (NS-4). */}
                            <S.Subtitle>Match bank deposits to the ERA &amp; EOB remits that explain them.</S.Subtitle>
                            <ReconcilePanel store={reconcile} confirm={confirm} />
                        </>
                    ) : (
                    <>
                    <S.Subtitle>Every remit received as an ERA or EOB.</S.Subtitle>
                    <Card size="large">
                            <>
                                <S.Filters>
                                    <ButtonGroup
                                        variant="track"
                                        buttons={kindButtons}
                                        selected={kindButtons[isEra ? 0 : 1]}
                                        onSelect={(button): void => store.setKind(button.label.startsWith('EOB') ? 'EOB' : 'ERA')}
                                    />
                                    {isEra && (
                                        <S.FilterSlot width={200}>
                                            <BaseSelect
                                                // "All clearinghouses" is the empty choice; a clear button would only repeat it.
                                                required={true}
                                                aria-label="Clearinghouse"
                                                options={clearinghouseOptions}
                                                value={
                                                    clearinghouseOptions.find(
                                                        option => option.value === (store.filters.clearinghouseId || ''),
                                                    ) || clearinghouseOptions[0]
                                                }
                                                onChange={(option): void =>
                                                    store.setFilters({
                                                        clearinghouseId: (option as ISelectOption<string>)?.value || undefined,
                                                    })
                                                }
                                            />
                                        </S.FilterSlot>
                                    )}
                                    <FilterField icon={mdiDomain} placeholder="Payer name" value={payer} width={240} onChange={setPayer} />
                                    <FilterField icon={mdiPound} placeholder="Payment number" value={number} onChange={setNumber} />
                                    <FilterField
                                        icon={mdiCurrencyUsd}
                                        placeholder="Check amount"
                                        value={amount}
                                        width={150}
                                        errorMessage={amountInvalid ? 'Enter an amount like 1495.40' : undefined}
                                        onChange={setAmount}
                                    />
                                    <S.FilterSlot width={170}>
                                        <BaseSelect
                                            required={true}
                                            aria-label="Paid date"
                                            options={DATE_OPTIONS}
                                            value={DATE_OPTIONS.find(option => option.value === preset)}
                                            onChange={(option): void =>
                                                setPreset(((option as ISelectOption<string>)?.value || 'any') as DatePreset)
                                            }
                                        />
                                    </S.FilterSlot>
                                    {preset === 'custom' && (
                                        <>
                                            <FilterField
                                                icon={mdiCalendarOutline}
                                                placeholder="Paid from"
                                                type="date"
                                                width={160}
                                                value={from}
                                                max={to || undefined}
                                                onChange={setFrom}
                                            />
                                            <FilterField
                                                icon={mdiCalendarOutline}
                                                placeholder="Paid to"
                                                type="date"
                                                width={160}
                                                value={to}
                                                min={from || undefined}
                                                onChange={setTo}
                                            />
                                        </>
                                    )}
                                    <S.Shown>{store.loading ? 'Loading…' : `${store.rows.length} shown`}</S.Shown>
                                </S.Filters>
                                {store.clearinghouseFilterIgnored && (
                                    <S.NoticeSlot>
                                        <Notice tone="warning">
                                            The server returned ERAs from other clearinghouses too, so this list is not
                                            limited to the one selected.
                                        </Notice>
                                    </S.NoticeSlot>
                                )}
                                {store.clearinghouseFilterUnconfirmed && (
                                    <S.NoticeSlot>
                                        <Notice tone="warning">
                                            Some of these ERAs do not say which clearinghouse they came from, so the page
                                            cannot confirm the list is limited to the one selected.
                                        </Notice>
                                    </S.NoticeSlot>
                                )}
                                {store.error ? (
                                    <S.NoticeSlot>
                                        <Notice tone="attention">
                                            The remits could not be loaded: {store.error}{' '}
                                            <BaseButton variant="text" size="compact" onClick={(): void => void store.load()}>
                                                Try again
                                            </BaseButton>
                                        </Notice>
                                    </S.NoticeSlot>
                                ) : !ready ? null : (
                                    <DataTable
                                        key={tableKey}
                                        presenter={presenter}
                                        isLoading={store.loading}
                                        onRowClick={toggleRow}
                                        renderRowSubComponent={renderDetail}
                                        onFetch={onFetch}
                                        disableSorting={true}
                                        hideFiltering={true}
                                        hideSettings={true}
                                    />
                                )}
                            </>
                    </Card>
                    </>
                    )}
                </S.TableFrame>
            </Row>
        </Page>
    );
});
